import { useState } from 'react';
import { X } from 'lucide-react';
import { Resource, User, saveSubmission } from '../lib/mockData';

interface AssignmentSubmissionFormProps {
  assignment: Resource;
  onClose: () => void;
  onSuccess: () => void;
  currentUser: User;
}

export default function AssignmentSubmissionForm({ assignment, onClose, onSuccess, currentUser }: AssignmentSubmissionFormProps) {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submission = {
      id: `sub-${Date.now()}`,
      assignmentId: assignment.id,
      studentId: currentUser.username,
      studentName: currentUser.name,
      subject: assignment.subject,
      content,
      submittedAt: new Date().toISOString()
    };

    saveSubmission(submission);
    
    setTimeout(() => {
      setIsSubmitting(false);
      onSuccess();
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Submit Assignment</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {assignment.subject} - {assignment.title}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          {/* Assignment Content Preview */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Assignment Details:</h3>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <p><strong>Teacher:</strong> {assignment.teacherName}</p>
              <p><strong>Subject:</strong> {assignment.subject}</p>
              <p><strong>Description:</strong> {assignment.description}</p>
            </div>
          </div>

          {/* Submission Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Your Answer *
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white min-h-[300px]"
                placeholder="Enter your solution here. Show all your working and explain your reasoning..."
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Student: {currentUser.name} | Will be submitted to {assignment.teacherName}
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 px-4 rounded-lg transition-colors font-medium"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Assignment'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
